import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { messaggeCodFisc, regexCodFisc } from '../../../../features/ammin-controllo/ammin-controllo.constants';
import { controlloCaricaPresidente, formArrayRequiredHelper } from '../common/helpers/norme-validators.helper';
import { codiceFiscaleHelper, futureOrInconsistentDate, ruleGuard } from './amm-controllo-validators.helpers';
import { formPaths } from '@shared/constants/form-paths.const';
import { CaricaCode } from '../common/consts/carica-code.const';

const COLL_SIND_ARRAY_PATH = `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`;

const my_AllElemCollegioSindacaleRequiredHelper = (form: FormGroup, targetPath: string) => {
    const my_AllElemCollegioSindacaleFormArray = getFormArray(form, COLL_SIND_ARRAY_PATH);
    formArrayRequiredHelper(my_AllElemCollegioSindacaleFormArray, targetPath);
};

export const collegioSindacaleRules = (ckNormeTargetValue: CkNormeValues) => ({
    my_AllCollSindCognome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindCognome');
        });
    },

    my_AllCollSindNome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindNome');
        });
    },

    my_AllCollSindVia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindVia');
        });
    },

    my_AllCollSindProvincia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindProvincia');
        });
    },

    my_AllCollSindComune: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindComune');
        });
    },

    my_AllCollSindCap: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllElemCollegioSindacaleRequiredHelper(form, 'my_AllCollSindCap');
        });
    },

    my_AllCollSindCodFisc: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const my_AllAmminUnicoFormArray = getFormArray(form, COLL_SIND_ARRAY_PATH);

            my_AllAmminUnicoFormArray.controls.map((group: FormGroup) => {
                const my_AllCollSindCodFiscControl = getControl(group, 'my_AllCollSindCodFisc');
                const errors = my_AllCollSindCodFiscControl.errors ? my_AllCollSindCodFiscControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllCollSindCodFiscControl?.value), errors, 'my_AllCollSindCodFisc', {
                    status: REQUIRED_STATUS,
                });

                addRemoveError(!regexCodFisc.test(my_AllCollSindCodFiscControl?.value), errors, 'my_AllCollSindCodFisc', {
                    status: messaggeCodFisc,
                });

                my_AllCollSindCodFiscControl.setErrors(isEqual(errors, {}) ? null : errors);

                codiceFiscaleHelper(group, 'my_AllCollSindNome', 'my_AllCollSindCognome', 'my_AllCollSindCodFisc');
            });
        });
    },

    my_AllCollSindDataElezione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const my_AllCollegioSindFormArray = getFormArray(form, COLL_SIND_ARRAY_PATH);

            my_AllCollegioSindFormArray.controls.map((group: FormGroup) => {
                const my_AllCollSindDataElezioneControl = getControl(group, 'my_AllCollSindDataElezione');
                const errors = my_AllCollSindDataElezioneControl.errors ? my_AllCollSindDataElezioneControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllCollSindDataElezioneControl?.value), errors, 'my_AllCollSindDataElezione', {
                    status: REQUIRED_STATUS,
                });

                my_AllCollSindDataElezioneControl.setErrors(isEqual(errors, {}) ? null : errors);
                futureOrInconsistentDate(form, my_AllCollSindDataElezioneControl, 'my_AllCollSindDataElezione', ckNormeTargetValue);
            });
        });
    },

    my_AllCollSindCarica: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_AllElemCollegioSindacaleFormArray = getFormArray(amministrazioneControlloGroup, 'my_AllCollegioSindacale.my_AllElemCollegioSindacale');
            const my_StatoVerbale: string = getControl(form, 'my_StatoVerbale').value;
            const my_StatoAccertamento: string = getControl(form, 'my_StatoAccertamento').value;
            const ckProposte: string = getControl(form, 'my_SezioneI__Rilevazione.my_Conclusioni.my_CkProposte').value;
            my_AllElemCollegioSindacaleFormArray.controls.forEach((group: FormGroup) => {
                const my_AllCollSindCaricaControl = getControl(group, 'my_AllCollSindCarica');
                const my_AllCollSindCarica: string = my_AllCollSindCaricaControl.value;
                const errors = my_AllCollSindCaricaControl.errors ? my_AllCollSindCaricaControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllCollSindCarica), errors, 'my_AllCollSindCarica', {
                    status: REQUIRED_STATUS,
                });

                addRemoveError(
                    my_AllCollSindCarica === CaricaCode.SINDACO && !(ckProposte === '3' && my_StatoAccertamento === '1' && my_StatoVerbale === '1'),
                    errors,
                    'my_AllCollSindCarica',
                    {
                        status: 'Declinare ulteriormente: sindaco effettivo o sindaco supplente',
                    }
                );

                my_AllCollSindCaricaControl.setErrors(isEqual(errors, {}) ? null : errors);
            });
            controlloCaricaPresidente(my_AllElemCollegioSindacaleFormArray, 'my_AllCollSindCarica');
        });
    },
});
